import Phaser from 'phaser';

export class HudRenderer {
  private textHealth: Phaser.GameObjects.Text;
  private textCUP: Phaser.GameObjects.Text;
  private textUSD: Phaser.GameObjects.Text;
  private textEUR: Phaser.GameObjects.Text;
  private textScore: Phaser.GameObjects.Text;
  private textMissionTitle: Phaser.GameObjects.Text;
  private textMissionProgress: Phaser.GameObjects.Text;
  /** Set by GameScene: toggles music/SFX mute, returns the new muted state. */
  public onToggleMute: (() => boolean) | null = null;

  private headerBg: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene) {
    const { width } = scene.scale.gameSize;

    // Dark header panel so the HUD (EUR/USD/CUP stats) is readable on the light sky
    this.headerBg = scene.add.rectangle(width / 2, 25, width, 50, 0x000000, 0.55)
      .setScrollFactor(0).setOrigin(0.5).setDepth(5);

    this.textHealth = scene.add.text(10, 8, '', {
      fontSize: '18px', fontFamily: 'monospace', color: '#ff4466',
    }).setLetterSpacing(6).setScrollFactor(0).setDepth(7);
    this.textCUP = scene.add.text(10, 26, '', {
      fontSize: '13px', fontFamily: 'monospace', color: '#ffd700', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setScrollFactor(0).setDepth(7);
    this.textUSD = scene.add.text(115, 26, '', {
      fontSize: '13px', fontFamily: 'monospace', color: '#00ff00', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setScrollFactor(0).setDepth(7);
    this.textEUR = scene.add.text(220, 26, '', {
      fontSize: '13px', fontFamily: 'monospace', color: '#00bfff', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setScrollFactor(0).setDepth(7);
    this.textScore = scene.add.text(width - 120, 6, '', {
      fontSize: '13px', fontFamily: 'monospace', color: '#ffffff', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setOrigin(1, 0).setScrollFactor(0).setDepth(7);
    this.textMissionTitle = scene.add.text(width / 2, 6, '', {
      fontSize: '12px', fontFamily: 'monospace', color: '#ffff00', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(7);
    this.textMissionProgress = scene.add.text(width / 2, 20, '', {
      fontSize: '11px', fontFamily: 'monospace', color: '#ffffff', backgroundColor: '#00000088', padding: { x: 3, y: 1 },
    }).setOrigin(0.5, 0).setScrollFactor(0).setDepth(7);

    // Mute button (music + SFX)
    this.muteBtn = scene.add.text(width - 30, 14, '♪', {
      fontSize: '20px', fontFamily: 'monospace', color: '#ffffff',
      backgroundColor: '#00000066', padding: { x: 6, y: 2 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(8).setInteractive({ useHandCursor: true });
    this.muteBtn.on('pointerdown', () => {
      if (!this.onToggleMute) return;
      this.setMuteVisual(this.onToggleMute());
    });
  }

  private muteBtn: Phaser.GameObjects.Text;

  public resize(width: number, invZoom = 1): void {
    const zx = (v: number) => v * invZoom;
    this.headerBg.setPosition(zx(width / 2), zx(25));
    (this.headerBg as unknown as { setDisplaySize: (w:number,h:number)=>void }).setDisplaySize?.(width * invZoom, 50 * invZoom);
    this.textHealth.setPosition(zx(10), zx(8)); this.textHealth.setScale(invZoom);
    this.textCUP.setPosition(zx(10), zx(26)); this.textCUP.setScale(invZoom);
    this.textUSD.setPosition(zx(115), zx(26)); this.textUSD.setScale(invZoom);
    this.textEUR.setPosition(zx(220), zx(26)); this.textEUR.setScale(invZoom);
    this.textScore.setPosition(zx(width - 120), zx(6)); this.textScore.setScale(invZoom);
    this.textMissionTitle.setPosition(zx(width / 2), zx(6)); this.textMissionTitle.setScale(invZoom);
    this.textMissionProgress.setPosition(zx(width / 2), zx(20)); this.textMissionProgress.setScale(invZoom);
    this.muteBtn.setPosition(zx(width - 30), zx(14)); this.muteBtn.setScale(invZoom);
  }

  public getHudObjects(): Phaser.GameObjects.GameObject[] {
    return [this.headerBg, this.textHealth, this.textCUP, this.textUSD, this.textEUR, this.textScore, this.textMissionTitle, this.textMissionProgress, this.muteBtn];
  }

  /** Reflect the persisted muted state on the button. */
  public setMuteVisual(muted: boolean): void {
    this.muteBtn.setText(muted ? '✕' : '♪');
    this.muteBtn.setColor(muted ? '#ff5555' : '#ffffff');
  }

  update(wallet: { cup: number; usd: number; eur: number; valueInCup(): number }, score: { getScore(): number }, missions: Array<{ isComplete(): boolean; type: string; target: number | string; getProgress(): number }>, playerState: { hp: number; maxHp: number }): void {

    // Health: 5 hearts (each = maxHp/5)
    const HEARTS = 5;
    const perHeart = playerState.maxHp / HEARTS;
    const filled = Math.min(HEARTS, Math.max(0, Math.ceil(playerState.hp / perHeart)));
    this.textHealth.setText('♥'.repeat(filled) + '♡'.repeat(HEARTS - filled));

    // Currency
    this.textCUP.setText(`CUP: ${wallet.cup}`);
    this.textUSD.setText(`USD: ${wallet.usd}`);
    this.textEUR.setText(`EUR: ${wallet.eur}`);

    // Score
    this.textScore.setText(`SCORE: ${score.getScore()}`);

    // Mission (only if active)
    const activeMission = missions.find((m) => !m.isComplete());
    if (activeMission) {
      this.textMissionTitle.setText(this.formatMissionTitle(activeMission.type));
      this.textMissionProgress.setText(this.formatMissionProgress(activeMission));
    } else {
      this.textMissionTitle.setText('');
      this.textMissionProgress.setText('');
    }
  }

  private formatMissionTitle(type: string): string {
    switch (type) {
      case 'MONEY_CUP': return 'ESCAPA DEL BARRIO';
      case 'REACH_X': return 'CRUCE DE CALLE';
      case 'COLLECT_ITEM': return 'COLECCIÓN';
      case 'INTERACT': return 'ENCUENTRO';
      default: return 'MISIÓN';
    }
  }

  private formatMissionProgress(mission: { type: string; target: number | string; getProgress(): number }): string {
    const progress = Math.round(mission.getProgress());
    const target = mission.target as number;
    switch (mission.type) {
      case 'MONEY_CUP':
        return `Dinero: ${this.formatNumber(progress)} / ${this.formatNumber(target)} CUP`;
      case 'REACH_X':
        return `Distancia: ${this.formatNumber(progress)} / ${this.formatNumber(target)} m`;
      case 'COLLECT_ITEM':
        return `${progress} / ${target} recogidos`;
      case 'INTERACT':
        return `${progress} / ${target} interactuados`;
      default:
        return `${progress} / ${target}`;
    }
  }

  private formatNumber(n: number): string {
    return n.toLocaleString('es-ES');
  }
}
